import React, { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { formatNumber, formatPrice } from '#/lib/display/formatting';
import {
  useSelectMarketStepSize,
  useSelectMarketTickSize,
} from '#/reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from '#/reduxState/store';

import type { HoveredRow, OrderbookSum } from './types';

const Wrap = styled.div`
  position: fixed;
  width: 250px;
  height: 100px;
  z-index: 99;
  background: var(--gray0c);
  border: 1px solid var(--gray2a);
  border-radius: 5px;
  padding: 0 12px;
  font-size: var(--small);

  white-space: nowrap;
  display: grid;
  align-content: center;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(3, min-content);
  gap: 11px;
`;

const Value = styled.div`
  font-weight: bold;
  text-align: right;
`;

const ValuePair = ({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) => {
  return (
    <>
      <div>{title}</div>
      <Value>{children}</Value>
    </>
  );
};

export default function SummaryTooltip({
  xOffset,
  yOffset,
  rowsSumAsks,
  rowsSumBids,
  selectedRow,
}: {
  xOffset?: number;
  yOffset: number;
  rowsSumAsks?: OrderbookSum[];
  rowsSumBids?: OrderbookSum[];
  selectedRow: Pick<HoveredRow, 'type' | 'index'>;
}) {
  const currentMarket = useSelector(state => state.markets.currentMarket);
  const tickSize = useSelectMarketTickSize(currentMarket.pair);
  const stepSize = useSelectMarketStepSize(currentMarket.pair);

  const result =
    selectedRow.type === 'ask'
      ? rowsSumAsks?.[selectedRow.index]
      : rowsSumBids?.[selectedRow.index];

  const sumQuantity = result?.sumQuantity;
  const sumTotal = result?.sumTotal;

  const averagePrice = useMemo(
    () => (sumTotal && sumQuantity ? sumTotal.dividedBy(sumQuantity) : null),
    [sumQuantity, sumTotal]
  );

  if (!xOffset) {
    return null;
  }

  // Note that Arrow must be rendered inside Orderbook row
  // to prevent flashing while moving over with mouse on edges
  return (
    <Wrap style={{ top: yOffset - 40 + 3, left: xOffset - 250 }}>
      <ValuePair title={`Sum ${currentMarket.base}:`}>
        {sumQuantity &&
          formatNumber(sumQuantity, {
            fixedDecimals: stepSize?.visibleDecimals,
          })}
      </ValuePair>
      <ValuePair title={`Sum ${currentMarket.quote}:`}>
        {sumTotal &&
          formatNumber(sumTotal, {
            fixedDecimals: tickSize?.visibleDecimals,
          })}
      </ValuePair>
      <ValuePair title="Price:">
        {averagePrice && formatPrice(averagePrice, tickSize?.visibleDecimals)}
      </ValuePair>
    </Wrap>
  );
}

// Functional single item calculation.
// Replaced by calculation of all rows in one go - more computation, but good caching.
// const { averagePrice, sumTotal, sumQuantity } = useMemo(() => {
//   const selected = selectedType === 'ask' ? asks[selectedIndex] : bids[selectedIndex];
//   const rows = selectedType === 'ask' ? asks.slice(0).reverse() : bids;
//   let sumTotal = BigNumberJs(0);
//   let sumQuantity = BigNumberJs(0);
//   rows.some(row => {
//     sumTotal = sumTotal.plus(row.total);
//     sumQuantity = sumQuantity.plus(row.quantity);
//     if (row.price === selected.price) {
//       return true;
//     }
//     return false;
//   });
//   const averagePrice = sumTotal.dividedBy(sumQuantity);
//   return { averagePrice, sumTotal, sumQuantity };
// }, [selectedType, asks, selectedIndex, bids]);
